import React from 'react';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import { Typography } from '@mui/material';

function resolve(obj, path) {
  return path.split('.').reduce((o, key) => o && o[key], obj);
}
export const desc = (a, b, orderBy) => {
  let resolvea = resolve(a, orderBy);
  let resolveb = resolve(b, orderBy);
  resolvea = resolvea ? resolvea : 'NA';
  resolveb = resolveb ? resolveb : 'NA';
  if (resolvea < resolveb) {
    return -1;
  }
  if (resolvea > resolveb) {
    return 1;
  }
  return 0;
};

export const stableSort = (array, cmp) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

export const getSorting = (order, orderBy) => {
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
};

export const EnhancedTableHead = (props) => {
  const createSortHandler = (property) => (event) => {
    props.onRequestSort(event, property);
  };

  const { headCells, order, orderBy } = props;

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            sx={{
              backgroundColor: '#2d186c',
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '1.1rem',
              borderBottom: '2px solid #222',
              letterSpacing: 0.5,
            }}
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
            nowrap="nowrap">
            {headCell.hasOwnProperty('noSort') ? (
              <span>{headCell.label}</span>
            ) : (
              <TableSortLabel
                sx={{
                  '& .MuiTableSortLabel-root': {
                    color: '#fff !important',
                    fontSize: '1.1rem',
                  },
                  '& .MuiTableSortLabel-icon': {
                    color: '#fff !important',
                  },
                  '&.Mui-active': {
                    color: '#fff',
                    fontSize: '1.1rem',
                  },
                }}
                active={orderBy === headCell.id}
                direction={order}
                onClick={createSortHandler(headCell.id)}>
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Typography
                    variant="body1"
                    sx={{
                      border: '0px',
                      clip: 'rect(0 0 0 0)',
                      height: '1px',
                      margin: '-1px',
                      overflow: 'hidden',
                      padding: '0px',
                      position: 'absolute',
                      top: '20px',
                      width: '1px',
                    }}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Typography>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  headCells: PropTypes.array.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
};

export const EnhancedTable = (props) => {
  const {
    children,
    headCells,
    order,
    orderBy,
    count,
    page,
    rowsPerPage,
    rowsPerPageOptions,
    savePaginationState,
    backendPaginationCallback,
    paginationOnTop,
  } = props;

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === 'desc';
    let pagination = {
      order: isDesc ? 'asc' : 'desc',
      orderBy: property,
      page: page,
      rowsPerPage: rowsPerPage,
    };
    savePaginationState(pagination);
  };

  const handleChangePage = (event, newPage) => {
    let pagination = {
      order: order,
      orderBy: orderBy,
      page: newPage,
      rowsPerPage: rowsPerPage,
    };
    savePaginationState(pagination);
    backendPaginationCallback && backendPaginationCallback(pagination);
  };

  const handleChangeRowsPerPage = (event) => {
    let pagination = {
      order: order,
      orderBy: orderBy,
      page: 0,
      rowsPerPage: +event.target.value,
    };
    savePaginationState(pagination);
    backendPaginationCallback && backendPaginationCallback(pagination);
  };

  const tablePagination = (
    <TablePagination
      rowsPerPageOptions={rowsPerPageOptions}
      component="div"
      count={count} 
      labelRowsPerPage="ROWS PER PAGE"
      rowsPerPage={rowsPerPage}
      page={page}
      backIconButtonProps={{
        'aria-label': 'previous page',
      }}
      nextIconButtonProps={{
        'aria-label': 'next page',
      }}
      sx={{
        '& .MuiTablePagination-root': {
          color: '#333333',
          fontSize: '14px',
        },
        '& .MuiTablePagination-caption': {
          color: '#000',
          fontSize: '14px',
        },
        '& .MuiTablePagination-selectRoot': {
          color: '#000',
          fontSize: '14px',
          fontWeight: 700,
        },
        '& .MuiTablePagination-selectLabel': {
          fontWeight: 700,
        },
        '& .MuiTablePagination-select': {
          color: '#000',
          fontSize: '14px',
        },
        '& .MuiTablePagination-selectIcon': {
          color: '#000',
        },
        '& .MuiTablePagination-input': {
          color: '#000',
          fontSize: '14px',
        },
        '& .MuiTablePagination-menuItem': {
          color: '#000',
          fontSize: '14px',
        },
        '& .MuiTablePagination-spacer': {
          flex: paginationOnTop ? 'none' : '1 1 100%', // Conditional styling for spacer
        },
      }}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
    />
  );

  return (
    <>
      {paginationOnTop && tablePagination}
      <Table
        stickyHeader={true}
        sx={{
          minWidth: '650px',
          border: 'none',
          borderColor: 'black',
          '& .MuiTableCell-body': {
            borderBottom: '1px solid #e0e0e0',
          },
        }}
        size="medium">
        <EnhancedTableHead
          headCells={headCells}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
        />
        {children}
      </Table>
      {!paginationOnTop && tablePagination}
    </>
  );
};

EnhancedTable.propTypes = {
  children: PropTypes.node.isRequired, // <TableBody>
  headCells: PropTypes.array.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  count: PropTypes.number,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  rowsPerPageOptions: PropTypes.array,
  savePaginationState: PropTypes.func.isRequired,
  backendPaginationCallback: PropTypes.func,
  paginationOnTop: PropTypes.bool,
};
